/**
 * Portfolio Construction — AI-powered portfolio builder
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, CircularProgress, Slider, Tooltip, IconButton,
  Button, alpha, Collapse, Skeleton,
} from '@mui/material';
import {
  InfoOutlined, TrendingUp, TrendingDown, TrendingFlat, AutoAwesome,
  ShowChart, TuneOutlined, AccountBalanceWalletOutlined, ExpandMore,
  SettingsOutlined,
} from '@mui/icons-material';
import { portfolioAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import PortfolioSummaryCards from '../components/portfolio/PortfolioSummaryCards';
import PortfolioTabs from '../components/portfolio/PortfolioTabs';

// ── Regime config ──
const REGIME_CFG = {
  RISK_ON: { Icon: TrendingUp, color: '#10b981' },
  RECOVERY: { Icon: TrendingUp, color: '#8bc34a' },
  SIDEWAY: { Icon: TrendingFlat, color: '#f59e0b' },
  NEUTRAL: { Icon: TrendingFlat, color: '#f59e0b' },
  RISK_OFF: { Icon: TrendingDown, color: '#ef4444' },
};

// ── Compact Parameter Slider ──
const ParamSlider = ({ label, value, onChange, min, max, step = 1, format, tooltip }) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.72rem', color: '#475569' }}>{label}</Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="top">
            <IconButton size="small" sx={{ p: 0 }}>
              <InfoOutlined sx={{ fontSize: 11, color: '#94a3b8' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#667eea', fontFamily: 'monospace' }}>
        {format ? format(value) : value}
      </Typography>
    </Box>
    <Slider
      value={value} onChange={(_, v) => onChange(v)}
      min={min} max={max} step={step} size="small"
      sx={{
        '& .MuiSlider-thumb': { width: 12, height: 12, bgcolor: '#667eea' },
        '& .MuiSlider-track': { background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)', height: 3 },
        '& .MuiSlider-rail': { bgcolor: alpha('#667eea', 0.1), height: 3 },
      }}
    />
  </Box>
);

// ══════════════════════════════════════
function PortfolioConstruction() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'vi';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [portfolio, setPortfolio] = useState(null);
  const [currentRegime, setCurrentRegime] = useState(null);
  const [marketBreadth, setMarketBreadth] = useState(null);
  const [sectorRotation, setSectorRotation] = useState(null);
  const [killSwitch, setKillSwitch] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [safeInvestRegime, setSafeInvestRegime] = useState(null);
  const [safeInvestEnabled, setSafeInvestEnabled] = useState(true);
  const [backtestResults, setBacktestResults] = useState(null);
  const [backtestLoading, setBacktestLoading] = useState(false);
  const [backtestError, setBacktestError] = useState('');

  // Parameters
  const [topSectors, setTopSectors] = useState(11);
  const [stocksPerSector, setStocksPerSector] = useState(3);
  const [capSingle, setCapSingle] = useState(0.15);
  const [capSector, setCapSector] = useState(0.40);
  const [lookbackDays, setLookbackDays] = useState(60);
  const [stretchThreshold, setStretchThreshold] = useState(0.06);
  const [atrZThreshold, setAtrZThreshold] = useState(1.5);
  const [rsiThreshold, setRsiThreshold] = useState(75);
  const [entryScoreMin, setEntryScoreMin] = useState(70);
  const [mddThreshold, setMddThreshold] = useState(0.08);
  const [backtestStartDate, setBacktestStartDate] = useState('2024-01-01');
  const [backtestEndDate, setBacktestEndDate] = useState('2024-12-31');
  const [backtestInitialCapital] = useState(1000000000);
  const [backtestRebalanceFrequency, setBacktestRebalanceFrequency] = useState('monthly');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [backtestOpen, setBacktestOpen] = useState(false);

  useEffect(() => {
    fetchRegimeInfo(); fetchMarketBreadth(); fetchSectorRotation(); fetchKillSwitch(); fetchSafeInvestRegime();
  }, []); // eslint-disable-line

  const fetchRegimeInfo = async () => { try { const r = await portfolioAPI.getRegimeInfo(); if (r.data.success) setCurrentRegime(r.data.current_regime); } catch (e) { console.error(e); } };
  const fetchMarketBreadth = async () => { try { const r = await portfolioAPI.getMarketBreadth(); if (r.data.success) setMarketBreadth(r.data); } catch (e) { console.error(e); } };
  const fetchSafeInvestRegime = async () => { try { const r = await portfolioAPI.getSafeInvestRegime(); if (r.data.success) setSafeInvestRegime(r.data.data); } catch (e) { console.error(e); setSafeInvestEnabled(false); } };
  const fetchSectorRotation = async () => { try { const r = await portfolioAPI.getSectorRotation(); if (r.data.success) setSectorRotation(r.data); } catch (e) { console.error(e); } };
  const fetchKillSwitch = async () => { try { const r = await portfolioAPI.getKillSwitchStatus(); if (r.data.success) setKillSwitch(r.data); } catch (e) { console.error(e); } };

  const recommendPortfolio = async () => {
    setLoading(true); setError(''); setPortfolio(null);
    try {
      const r = await portfolioAPI.recommendPortfolio({ topSectors, stocksPerSector, capSingle, capSector, lookbackDays, stretchThreshold, atrZThreshold, rsiThreshold, entryScoreMin, mddThreshold });
      if (r.data && (r.data.portfolio || r.data.watchlist || r.data.sectors)) {
        setPortfolio(r.data);
        if (r.data.market?.regime) setCurrentRegime(r.data.market.regime);
        fetchMarketBreadth(); fetchSectorRotation(); fetchKillSwitch();
        setActiveTab('buy-now');
      } else setError(r.data?.message || 'Failed to generate portfolio');
    } catch (err) { setError(err.response?.data?.message || 'Failed to generate portfolio'); }
    finally { setLoading(false); }
  };

  const runBacktest = async () => {
    setBacktestLoading(true); setBacktestError(''); setBacktestResults(null);
    try {
      const r = await portfolioAPI.runBacktest({ startDate: backtestStartDate, endDate: backtestEndDate, initialCapital: backtestInitialCapital, rebalanceFrequency: backtestRebalanceFrequency, topSectors, stocksPerSector, capSingle, capSector, lookbackDays });
      if (r.data.success) { setBacktestResults(r.data); setActiveTab('overview'); }
      else setBacktestError(r.data.error || 'Backtest failed');
    } catch (err) { setBacktestError(err.response?.data?.error || 'Backtest failed'); }
    finally { setBacktestLoading(false); }
  };

  const rc = currentRegime ? REGIME_CFG[currentRegime] : null;

  return (
    <Box sx={{ p: 2.5, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 2.5, flexShrink: 0,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 3px 10px ${alpha('#667eea', 0.3)}`,
          }}>
            <AccountBalanceWalletOutlined sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
              {t('riskManagement.portfolioConstruction.title')}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500, lineHeight: 1.3, mt: 0.3 }}>
              {t('riskManagement.portfolioConstruction.subtitle')}
            </Typography>
          </Box>
        </Box>
        {rc && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.8, borderRadius: 2, bgcolor: alpha(rc.color, 0.08), border: `1px solid ${alpha(rc.color, 0.15)}` }}>
            <rc.Icon sx={{ fontSize: 18, color: rc.color }} />
            <Box>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {t('riskManagement.portfolioConstruction.regime.marketRegime')}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: rc.color, lineHeight: 1.2 }}>
                {t(`riskManagement.portfolioConstruction.regime.${currentRegime === 'RISK_ON' ? 'riskOn' : currentRegime === 'NEUTRAL' ? 'neutral' : 'riskOff'}`)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* 2-column layout */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 2 }}>
        {/* ── Left Panel: Parameters ── */}
        <Box>
          <Box sx={{ position: 'sticky', top: 16, borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 2 }}>
            {/* Panel header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TuneOutlined sx={{ color: '#fff', fontSize: 17 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                  {t('riskManagement.portfolioConstruction.parameters')}
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                  {t('riskManagement.portfolioConstruction.parametersSubtitle')}
                </Typography>
              </Box>
            </Box>

            {/* Basic params grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
              <ParamSlider label={t('riskManagement.portfolioConstruction.topSectors')} value={topSectors} onChange={setTopSectors} min={1} max={11} tooltip={t('riskManagement.portfolioConstruction.tooltips.topSectors')} />
              <ParamSlider label={t('riskManagement.portfolioConstruction.stocksPerSector')} value={stocksPerSector} onChange={setStocksPerSector} min={1} max={5} tooltip={t('riskManagement.portfolioConstruction.tooltips.stocksPerSector')} />
              <ParamSlider label="Max/CP" value={capSingle} onChange={setCapSingle} min={0.05} max={0.30} step={0.01} format={v => `${(v*100).toFixed(0)}%`} tooltip={t('riskManagement.portfolioConstruction.tooltips.capSingle.title')} />
              <ParamSlider label="Max/Ngành" value={capSector} onChange={setCapSector} min={0.20} max={0.60} step={0.05} format={v => `${(v*100).toFixed(0)}%`} tooltip={t('riskManagement.portfolioConstruction.tooltips.capSector.title')} />
              <ParamSlider label="Lookback" value={lookbackDays} onChange={setLookbackDays} min={30} max={120} step={10} format={v => `${v}d`} tooltip={t('riskManagement.portfolioConstruction.tooltips.lookback')} />
              <ParamSlider label="Entry Score" value={entryScoreMin} onChange={setEntryScoreMin} min={60} max={100} step={5} tooltip={t('riskManagement.portfolioConstruction.tooltips.entryScoreMin.title')} />
            </Box>

            {/* Advanced - collapsible */}
            <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', overflow: 'hidden', mb: 2 }}>
              <Box onClick={() => setAdvancedOpen(!advancedOpen)} sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 1.5, py: 1, cursor: 'pointer', '&:hover': { bgcolor: alpha('#667eea', 0.04) },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <SettingsOutlined sx={{ fontSize: 14, color: '#667eea' }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#667eea' }}>
                    {t('riskManagement.portfolioConstruction.advancedSettings')}
                  </Typography>
                </Box>
                <ExpandMore sx={{ fontSize: 18, color: '#667eea', transform: advancedOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
              </Box>
              <Collapse in={advancedOpen}>
                <Box sx={{ p: 1.5, pt: 0.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <ParamSlider label="Stretch" value={stretchThreshold} onChange={setStretchThreshold} min={0.03} max={0.10} step={0.01} format={v => `${(v*100).toFixed(0)}%`} tooltip={t('riskManagement.portfolioConstruction.tooltips.stretchThreshold.title')} />
                  <ParamSlider label="ATR Z" value={atrZThreshold} onChange={setAtrZThreshold} min={1.0} max={2.5} step={0.1} format={v => v.toFixed(1)} tooltip={t('riskManagement.portfolioConstruction.tooltips.atrZThreshold.title')} />
                  <ParamSlider label="Max RSI" value={rsiThreshold} onChange={setRsiThreshold} min={65} max={85} step={5} tooltip={t('riskManagement.portfolioConstruction.tooltips.rsiThreshold.title')} />
                  <ParamSlider label="MDD Max" value={mddThreshold} onChange={setMddThreshold} min={0.05} max={0.15} step={0.01} format={v => `${(v*100).toFixed(0)}%`} tooltip={t('riskManagement.portfolioConstruction.tooltips.mddThreshold.title')} />
                </Box>
              </Collapse>
            </Box>

            {/* Error */}
            {error && (
              <Box sx={{ px: 1.5, py: 0.8, mb: 1.5, borderRadius: 1.5, bgcolor: alpha('#ef4444', 0.06), border: `1px solid ${alpha('#ef4444', 0.15)}` }}>
                <Typography sx={{ fontSize: '0.76rem', color: '#ef4444' }}>{error}</Typography>
              </Box>
            )}

            {/* Build button */}
            <Button fullWidth variant="contained" size="large"
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesome sx={{ fontSize: 18 }} />}
              onClick={recommendPortfolio} disabled={loading}
              sx={{
                py: 1.2, fontWeight: 700, textTransform: 'none', fontSize: '0.9rem', borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: `0 4px 14px ${alpha('#667eea', 0.35)}`,
                '&:hover': { boxShadow: `0 6px 20px ${alpha('#667eea', 0.45)}` },
              }}>
              {loading ? t('riskManagement.portfolioConstruction.building') : t('riskManagement.portfolioConstruction.buildPortfolio')}
            </Button>

            {/* Backtest - collapsible */}
            <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', overflow: 'hidden', mt: 2 }}>
              <Box onClick={() => setBacktestOpen(!backtestOpen)} sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 1.5, py: 1, cursor: 'pointer', '&:hover': { bgcolor: alpha('#10b981', 0.04) },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ShowChart sx={{ fontSize: 14, color: '#10b981' }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#10b981' }}>
                    {t('backtest.config.title')}
                  </Typography>
                </Box>
                <ExpandMore sx={{ fontSize: 18, color: '#10b981', transform: backtestOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
              </Box>
              <Collapse in={backtestOpen}>
                <Box sx={{ p: 1.5, pt: 0.5 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.7rem', color: '#475569', mb: 0.3 }}>{t('backtest.config.startDate')}</Typography>
                      <input type="date" value={backtestStartDate} onChange={e => setBacktestStartDate(e.target.value)}
                        style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.78rem', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.7rem', color: '#475569', mb: 0.3 }}>{t('backtest.config.endDate')}</Typography>
                      <input type="date" value={backtestEndDate} onChange={e => setBacktestEndDate(e.target.value)}
                        style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.78rem', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                    </Box>
                  </Box>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.7rem', color: '#475569', mb: 0.3 }}>{t('backtest.config.rebalanceFrequency')}</Typography>
                    <select value={backtestRebalanceFrequency} onChange={e => setBacktestRebalanceFrequency(e.target.value)}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.78rem', fontFamily: 'inherit', backgroundColor: '#fff' }}>
                      <option value="daily">{t('backtest.config.frequencies.daily')}</option>
                      <option value="weekly">{t('backtest.config.frequencies.weekly')}</option>
                      <option value="monthly">{t('backtest.config.frequencies.monthly')}</option>
                      <option value="quarterly">{t('backtest.config.frequencies.quarterly')}</option>
                    </select>
                  </Box>
                  {backtestError && (
                    <Box sx={{ px: 1.5, py: 0.5, mb: 1, borderRadius: 1, bgcolor: alpha('#ef4444', 0.06), border: `1px solid ${alpha('#ef4444', 0.15)}` }}>
                      <Typography sx={{ fontSize: '0.72rem', color: '#ef4444' }}>{backtestError}</Typography>
                    </Box>
                  )}
                  <Button fullWidth variant="outlined" size="small"
                    startIcon={backtestLoading ? <CircularProgress size={14} color="inherit" /> : <ShowChart sx={{ fontSize: 16 }} />}
                    onClick={runBacktest} disabled={backtestLoading}
                    sx={{ py: 0.8, fontWeight: 700, textTransform: 'none', fontSize: '0.82rem', borderRadius: 1.5, borderColor: '#10b981', color: '#10b981', '&:hover': { borderColor: '#059669', bgcolor: alpha('#10b981', 0.04) } }}>
                    {backtestLoading ? t('backtest.config.running') : t('backtest.config.run')}
                  </Button>
                </Box>
              </Collapse>
            </Box>
          </Box>
        </Box>

        {/* ── Right Panel: Results ── */}
        <Box>
          {/* Empty state */}
          {!portfolio && !loading && (
            <Box sx={{ py: 8, textAlign: 'center', borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
              <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#f8fafc', border: '1.5px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                <AutoAwesome sx={{ fontSize: 24, color: '#cbd5e1' }} />
              </Box>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#94a3b8', mb: 0.5 }}>
                {t('riskManagement.portfolioConstruction.emptyState.title')}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#cbd5e1', maxWidth: 400, mx: 'auto' }}>
                {t('riskManagement.portfolioConstruction.emptyState.description')}
              </Typography>
            </Box>
          )}

          {/* Loading */}
          {loading && (
            <Box sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 3 }}>
              <Skeleton height={80} sx={{ borderRadius: 2, mb: 1.5 }} />
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 2 }}>
                {[0,1,2,3].map(i => <Skeleton key={i} height={70} sx={{ borderRadius: 2 }} />)}
              </Box>
              {[...Array(6)].map((_, i) => <Skeleton key={i} height={36} sx={{ mb: 0.5, borderRadius: 1 }} />)}
            </Box>
          )}

          {/* Results */}
          {portfolio && (
            <Box>
              {/* Safe Invest Regime Banner */}
              {safeInvestEnabled && safeInvestRegime && (() => {
                const regime = safeInvestRegime.regime || 'SIDEWAY';
                const score = safeInvestRegime.market_score || 0;
                const rcfg = REGIME_CFG[regime] || REGIME_CFG.NEUTRAL;
                return (
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 2, py: 1.2, mb: 1.5, borderRadius: 2,
                    bgcolor: alpha(rcfg.color, 0.06), border: `1px solid ${alpha(rcfg.color, 0.15)}`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <rcfg.Icon sx={{ fontSize: 20, color: rcfg.color }} />
                      <Box>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.5px' }}>
                          Safe Invest Regime
                        </Typography>
                        <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: rcfg.color, lineHeight: 1.2 }}>
                          {regime}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {safeInvestRegime.interpretation?.action && (
                        <Typography sx={{ fontSize: '0.76rem', color: '#64748b', maxWidth: 300, textAlign: 'right' }}>
                          {safeInvestRegime.interpretation.action}
                        </Typography>
                      )}
                      <Box sx={{ display: 'inline-flex', px: 1, py: 0.3, borderRadius: 1, bgcolor: alpha(rcfg.color, 0.12) }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: rcfg.color, fontFamily: 'monospace' }}>
                          {score.toFixed(0)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })()}

              {/* Summary Cards */}
              <Box sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 2, mb: 1.5 }}>
                <PortfolioSummaryCards portfolio={portfolio} />
              </Box>

              {/* Tabs */}
              <PortfolioTabs
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                portfolio={portfolio}
                killSwitch={killSwitch}
                marketBreadth={marketBreadth}
                sectorRotation={sectorRotation}
                currentLang={currentLang}
                backtestResults={backtestResults}
                backtestLoading={backtestLoading}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default PortfolioConstruction;
